import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Send, AlertTriangle } from 'lucide-react';
import { useT } from '../../../i18n';

/**
 * ChatInput component
 * Input area for typing and sending messages.
 *
 * T-148: `@` mentions for sheet names — typing `@` (at line start or after a
 * space) opens a dropdown that autocompletes sheet names. Selecting one (mouse
 * or Enter/Tab) inserts `@Name ` and registers the sheet id so the parent can
 * pass it as context when sending. Arrow keys navigate, Escape closes. While
 * the dropdown is open, Enter selects (it does NOT send).
 */
const ChatInput = ({ input, setInput, loading, sendMessage, sheets = [], mentionedSheetIds = [], setMentionedSheetIds, modelSelectorControl, effortSelectorControl, attachmentsControl, referencesControl }) => {
  const t = useT();
  // T-134: el disclaimer ocupaba 3 líneas. Colapsado a una sola con elipsis;
  // "ver más" lo expande. Ahorra espacio sin esconder el aviso.
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  // T-148: mention dropdown state
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionAt, setMentionAt] = useState(-1);   // index of the `@` char
  const [mentionCaret, setMentionCaret] = useState(0); // caret pos when detected
  const taRef = useRef(null);

  // Filtered sheet list — derived in render (not in state) so it always
  // reflects the latest query. Capped at 8 for a compact dropdown.
  const mentionMatches = mentionOpen
    ? sheets
        .filter((s) => s.name && s.name.toLowerCase().includes(mentionQuery.toLowerCase()))
        .slice(0, 8)
    : [];

  // Insert a selected sheet mention: replace the `@query` span with
  // `@Name ` and remember the id for the upcoming send.
  const selectMention = (sheet) => {
    const val = input;
    const start = mentionAt;
    const end = mentionCaret;
    const nuevo = val.slice(0, start) + '@' + sheet.name + ' ' + val.slice(end);
    setInput(nuevo);
    setMentionedSheetIds((prev) => (prev.includes(sheet.id) ? prev : [...prev, sheet.id]));
    setMentionOpen(false);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setInput(val);

    // Detect an active `@` mention at the caret position.
    const pos = e.target.selectionStart;
    const before = val.slice(0, pos);
    const at = before.lastIndexOf('@');
    if (at !== -1) {
      const charBefore = before[at - 1];
      const isValidStart = charBefore === undefined || /\s/.test(charBefore);
      const segment = before.slice(at);
      const noNewline = !segment.includes('\n');
      if (isValidStart && noNewline) {
        const query = before.slice(at + 1);
        setMentionQuery(query);
        setMentionOpen(true);
        setMentionIndex(0);
        setMentionAt(at);
        setMentionCaret(pos);
        return;
      }
    }
    setMentionOpen(false);
  };

  const handleKeyDown = (e) => {
    if (mentionOpen && mentionMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => Math.min(mentionMatches.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectMention(mentionMatches[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }
    // No dropdown open: Enter (without Shift) sends the message — same as before.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setMentionOpen(false);
      sendMessage();
    }
  };

  return (
    <div className="flex-shrink-0 border-t border-white/20">
      {/* T-208: selector de modelo del chat (cerca del input/prompt). El control
          lo construye el padre (index.js) siguiendo el patrón de
          reasoningTemplateControl: Popover + Button + estado activo visible.
          Si la lista de chat-models está vacía el padre pasa null y no se
          renderiza nada. */}
      {/* T-226: selector de esfuerzo de razonamiento, junto al de modelo
          (mismo contenedor, mismo patrón). Siempre visible: el padre siempre
          pasa un control definido (default 'high'). */}
      <div className="px-4 pt-2 flex items-center gap-1">
        {modelSelectorControl}
        {effortSelectorControl}
        {/* T-239: control de adjuntos del contexto (comentarios + imágenes) */}
        {attachmentsControl}
        {/* T-242: control de referencias automáticas (popover con alcance) */}
        {referencesControl}
      </div>
      <div className="flex gap-2 p-4 pb-2">
        <div className="relative flex-1 min-w-0">
          {mentionMatches.length > 0 && (
            <div
              data-testid="mention-dropdown"
              className="absolute bottom-full left-0 right-0 mb-1 z-50 max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg"
            >
              {mentionMatches.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`mention-item-${s.id}`}
                  onMouseDown={(ev) => { ev.preventDefault(); selectMention(s); }}
                  className={`w-full text-left px-3 py-1.5 text-sm ${i === mentionIndex ? 'bg-sky-500 text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          <Textarea
            ref={taRef}
            data-testid="chat-input"
            placeholder="Escribe tu mensaje..."
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            rows={2}
            // T-125: resize vertical nativo — el grip del navegador aparece en la
            // esquina inferior derecha; min-h-[60px] del Textarea evita colapsarlo.
            className="resize-y"
            disabled={loading}
          />
        </div>
        <Button
          data-testid="send-message-button"
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 self-end flex flex-col items-center gap-0.5"
        >
          <Send className="w-4 h-4" />
          <span className="hidden sm:inline text-[10px]">Enviar</span>
        </Button>
      </div>
      <div
        data-testid="chat-disclaimer"
        className="flex items-start gap-1.5 px-4 pb-3 text-xs text-slate-500 dark:text-slate-400"
      >
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
        <span className={`min-w-0 flex-1 ${disclaimerOpen ? '' : 'truncate'}`}>
          {t('chat.disclaimer')}
        </span>
        <button
          type="button"
          onClick={() => setDisclaimerOpen((v) => !v)}
          className="flex-shrink-0 underline hover:text-slate-700 dark:hover:text-slate-200"
        >
          {disclaimerOpen ? t('chat.seeLess') : t('chat.seeMore')}
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
